import { Fragment, createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api } from './api';
import { setQueryCacheScope } from './query-cache';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member' | 'kid';
  color: string;
  must_change_password: number;
  google_linked: number;
  password_login_disabled: number;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Set when /auth/me fails for a reason other than 401. The saved session is kept. */
  authError: string | null;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Вход в демо: сервер создаёт песочницу и сессию, пароля нет. */
  loginDemo: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<User>('/auth/me'));
      setAuthError(null);
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 401) {
        setUser(null);
        setAuthError(null);
      } else {
        setAuthError(error instanceof Error ? error.message : 'Не удалось проверить вход');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await api.post<User>('/auth/login', { email, password });
    setAuthError(null);
    setUser(me);
  }, []);

  const loginDemo = useCallback(async () => {
    const me = await api.post<User>('/auth/demo', {});
    setAuthError(null);
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } finally {
      setAuthError(null);
      setUser(null);
    }
  }, []);

  const scope = user ? `${user.id}:${user.role}` : 'anonymous';
  setQueryCacheScope(scope);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authError,
        mustChangePassword: Boolean(user?.must_change_password),
        login,
        loginDemo,
        logout,
        refresh,
      }}
    >
      <Fragment key={scope}>{children}</Fragment>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth вызван вне AuthProvider');
  return ctx;
}

export { ApiError };
